import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import DemographicsSection from "../components/DemographicsSection";
import SendLinkButton from "../components/SendLinkButton";

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function NewDeployment() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [orgs, setOrgs] = useState([]);
  const [orgMode, setOrgMode] = useState("existing");  // "existing" | "new"
  const [orgId, setOrgId] = useState("");
  const [newOrg, setNewOrg] = useState({ name: "", slug: "", industry: "", role_type: "" });

  // Sprint 11 — Individual Deployment Mode toggle. "organization" keeps
  // the team/cohort flow (demographics, anonymous aggregation). "individual"
  // is for 1:1 coaching — skip demographics, collect client name/email,
  // require an assigned coach. LOCKED after creation (backend rejects
  // edits via SettingsUpdate).
  const [deploymentMode, setDeploymentMode] = useState("organization");
  const [coachOptions, setCoachOptions] = useState([]);
  const [assignedCoachId, setAssignedCoachId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [generatedToken, setGeneratedToken] = useState("");

  const [name, setName] = useState("");
  const [waveNumber, setWaveNumber] = useState(1);
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [individualReports, setIndividualReports] = useState(false);
  // Sprint 9 — each row carries a stable `_id` so React can preserve
  // input focus / cursor position when rows above are removed. Using
  // the array index causes the focused input's value to follow the
  // wrong row after a delete.
  const [demos, setDemos] = useState([
    { _id: "demo-init-0", key: "team", values: "" },
    { _id: "demo-init-1", key: "role", values: "" },
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdDeploymentId, setCreatedDeploymentId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/organizations");
        setOrgs(data.organizations || []);
        if ((data.organizations || []).length > 0) {
          setOrgId(data.organizations[0].id);
        } else {
          setOrgMode("new");
        }
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  // Load assignable coaches when the consultant switches to individual mode.
  // Pre-fill with the current user — the spec says "defaults to the
  // current user" since they're usually the one creating the engagement.
  useEffect(() => {
    if (deploymentMode !== "individual") return;
    if (coachOptions.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/api/users/coaches");
        const coaches = data.coaches || [];
        setCoachOptions(coaches);
        if (user?.id && coaches.some((c) => c.id === user.id)) {
          setAssignedCoachId(user.id);
        } else if (coaches.length > 0) {
          setAssignedCoachId(coaches[0].id);
        }
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [deploymentMode, coachOptions.length, user?.id]);

  function updateDemo(idx, field, value) {
    setDemos((d) => d.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  }
  function addDemoRow() {
    setDemos((d) => [...d, { _id: `demo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, key: "", values: "" }]);
  }
  function removeDemoRow(idx) {
    setDemos((d) => d.filter((_, i) => i !== idx));
  }

  function buildDemographicConfig() {
    const out = {};
    for (const row of demos) {
      const k = row.key.trim();
      if (!k) continue;
      // Sprint-10 hotfix — accept commas, newlines, or semicolons as
      // delimiters. Consultants typed one role per line in production
      // and only the first survived; this matches what they expect.
      const values = row.values
        .split(/[,\n;]+/)
        .map((v) => v.trim())
        .filter(Boolean);
      if (values.length === 0) continue;
      out[k] = values;
    }
    return out;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Deployment name is required.");
      return;
    }
    // Sprint 11 — Individual mode requires client + coach; demographics
    // are auto-skipped on the server side.
    if (deploymentMode === "individual") {
      if (!clientName.trim()) {
        setError("Client name is required for individual deployments.");
        return;
      }
      if (!clientEmail.trim()) {
        setError("Client email is required for individual deployments.");
        return;
      }
      if (!assignedCoachId) {
        setError("Assign a coach for this individual deployment.");
        return;
      }
    }
    let finalOrgId = orgId;
    if (orgMode === "new") {
      if (!newOrg.name.trim()) {
        setError("Organization name is required.");
        return;
      }
      const slug = newOrg.slug.trim() || slugify(newOrg.name);
      if (!slug) {
        setError("Organization slug could not be generated. Provide one explicitly.");
        return;
      }
      try {
        setSubmitting(true);
        const orgResp = await api.post("/api/organizations", {
          name: newOrg.name.trim(),
          slug,
          industry: newOrg.industry.trim() || null,
          role_type: newOrg.role_type.trim() || null,
        });
        finalOrgId = orgResp.data.id;
      } catch (e) {
        setSubmitting(false);
        setError(formatApiError(e));
        return;
      }
    } else if (!finalOrgId) {
      setError("Pick an organization.");
      return;
    }
    try {
      setSubmitting(true);
      const isIndividual = deploymentMode === "individual";
      const payload = {
        org_id: finalOrgId,
        name: name.trim(),
        wave_number: Number(waveNumber) || 1,
        // Demographics intentionally empty in individual mode — backend
        // rejects non-empty config for that branch.
        demographic_config: isIndividual ? {} : buildDemographicConfig(),
        individual_reports_enabled: individualReports,
        opens_at: opensAt ? new Date(opensAt).toISOString() : null,
        closes_at: closesAt ? new Date(closesAt).toISOString() : null,
        deployment_mode: deploymentMode,
        assigned_coach_id: isIndividual ? assignedCoachId : null,
        client_name: isIndividual ? clientName.trim() : null,
        client_email: isIndividual ? clientEmail.trim() : null,
      };
      const { data } = await api.post("/api/deployments", payload);
      if (isIndividual && data.respondent_token) {
        // Surface the client link immediately so the coach can share it.
        setGeneratedToken(data.respondent_token);
        setCreatedDeploymentId(data.id);
        // Skip the auto-redirect — let the coach copy the link first.
        setSubmitting(false);
        return;
      }
      navigate(`/dashboard/deployments/${data.id}?tab=respondents`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="new-deployment-page">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-3xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="btn-quiet text-xs" data-testid="back-to-dashboard">
              ← All deployments
            </button>
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-sm text-gray-700 hidden md:block">{user?.name}</div>
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">Sign out</button>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-3xl mx-auto px-8 py-12">
        <div className="mb-10">
          <div className="eyebrow mb-2">New deployment</div>
          <h1 style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>Configure the wave.</h1>
          <p className="lead text-base md:text-lg mt-3 max-w-prose">
            Deployments are created in draft. You&rsquo;ll add respondents on the next screen and open the
            survey for responses when you&rsquo;re ready.
          </p>
        </div>

        {error && (
          <div className="panel mb-6" style={{ borderColor: "#AD393E" }} data-testid="form-error">
            <div className="eyebrow mb-1" style={{ color: "#AD393E" }}>Couldn&rsquo;t create deployment</div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-10">
          {/* Sprint 11 — Mode toggle. First decision on the wizard.
              Locked after creation; presented as two cards with the
              spec's verbatim descriptions. */}
          <section data-testid="mode-section">
            <h2 className="text-base mb-4" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", color: "#2F6B7C" }}>
              Deployment mode
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDeploymentMode("organization")}
                data-testid="mode-organization"
                aria-pressed={deploymentMode === "organization"}
                style={{
                  textAlign: "left",
                  padding: "16px 18px",
                  background: deploymentMode === "organization" ? "#FBFAF7" : "white",
                  border: `2px solid ${deploymentMode === "organization" ? "#2F6B7C" : "#E0E0E0"}`,
                  cursor: "pointer",
                }}
              >
                <div className="eyebrow mb-1" style={{ color: deploymentMode === "organization" ? "#2F6B7C" : "#7F7F7F" }}>
                  Organizational Resilience Index · ORI
                </div>
                <div className="text-sm leading-snug text-ink">
                  For team or cohort assessments. Respondents stay anonymous; the team sees a group summary.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setDeploymentMode("individual")}
                data-testid="mode-individual"
                aria-pressed={deploymentMode === "individual"}
                style={{
                  textAlign: "left",
                  padding: "16px 18px",
                  background: deploymentMode === "individual" ? "#FBFAF7" : "white",
                  border: `2px solid ${deploymentMode === "individual" ? "#2563EB" : "#E0E0E0"}`,
                  cursor: "pointer",
                }}
              >
                <div className="eyebrow mb-1" style={{ color: deploymentMode === "individual" ? "#2563EB" : "#7F7F7F" }}>
                  Personal Resilience Index · PRI
                </div>
                <div className="text-sm leading-snug text-ink">
                  For coaching engagements with one client. The coach owns the data and the debrief.
                </div>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Mode is locked after the deployment is created.
            </p>
          </section>

          {/* Org */}
          <section data-testid="org-section">
            <h2 className="text-base mb-4" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", color: "#2F6B7C" }}>
              Organization
            </h2>
            {orgs.length > 0 && (
              <div className="mb-3">
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="radio"
                    checked={orgMode === "existing"}
                    onChange={() => setOrgMode("existing")}
                    data-testid="org-mode-existing"
                  />
                  <span className="text-sm">Existing organization</span>
                </label>
                {orgMode === "existing" && (
                  <select
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    data-testid="org-select"
                    aria-label="Select existing organization"
                    style={{
                      maxWidth: 360, padding: "10px 12px",
                      border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                    }}
                  >
                    {orgs.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <div>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="radio"
                  checked={orgMode === "new"}
                  onChange={() => setOrgMode("new")}
                  data-testid="org-mode-new"
                />
                <span className="text-sm">+ Create new organization</span>
              </label>
              {orgMode === "new" && (
                <div className="space-y-3 pl-6 border-l-2" style={{ borderColor: "#E8E8E8" }}>
                  <div>
                    <label className="label">Organization name</label>
                    <input
                      type="text"
                      value={newOrg.name}
                      onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value, slug: newOrg.slug || slugify(e.target.value) })}
                      placeholder="Acme Health"
                      data-testid="org-name"
                      style={{
                        width: "100%", maxWidth: 360, padding: "10px 12px",
                        border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                      }}
                    />
                  </div>
                  <div>
                    <label className="label">Slug</label>
                    <input
                      type="text"
                      value={newOrg.slug}
                      onChange={(e) => setNewOrg({ ...newOrg, slug: slugify(e.target.value) })}
                      placeholder="acme-health"
                      data-testid="org-slug"
                      style={{
                        width: "100%", maxWidth: 360, padding: "10px 12px",
                        border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                      }}
                    />
                  </div>
                  <div>
                    <label className="label">Industry <span className="text-gray-500 text-xs">(optional)</span></label>
                    <input
                      type="text"
                      value={newOrg.industry}
                      onChange={(e) => setNewOrg({ ...newOrg, industry: e.target.value })}
                      placeholder="professional_services"
                      data-testid="org-industry"
                      style={{
                        width: "100%", maxWidth: 360, padding: "10px 12px",
                        border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Deployment basics */}
          <section data-testid="deployment-section">
            <h2 className="text-base mb-4" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", color: "#2F6B7C" }}>
              Deployment
            </h2>
            <div className="space-y-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Q2 2026 Pulse"
                  data-testid="deployment-name"
                  style={{
                    width: "100%", maxWidth: 420, padding: "10px 12px",
                    border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label className="label">Wave number</label>
                <input
                  type="number"
                  min={1}
                  value={waveNumber}
                  onChange={(e) => setWaveNumber(e.target.value)}
                  data-testid="wave-number"
                  aria-label="Wave number"
                  style={{
                    width: "100px", padding: "10px 12px",
                    border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                  }}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Opens at <span className="text-gray-500 text-xs">(optional)</span></label>
                  <input
                    type="datetime-local"
                    value={opensAt}
                    onChange={(e) => setOpensAt(e.target.value)}
                    data-testid="opens-at"
                    aria-label="Opens at (optional)"
                    style={{
                      width: "100%", padding: "10px 12px",
                      border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label className="label">Closes at <span className="text-gray-500 text-xs">(optional)</span></label>
                  <input
                    type="datetime-local"
                    value={closesAt}
                    onChange={(e) => setClosesAt(e.target.value)}
                    data-testid="closes-at"
                    aria-label="Closes at (optional)"
                    style={{
                      width: "100%", padding: "10px 12px",
                      border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                    }}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  checked={individualReports}
                  onChange={(e) => setIndividualReports(e.target.checked)}
                  data-testid="individual-reports-toggle"
                />
                <span className="text-sm">Offer individual respondent reports at survey completion</span>
              </label>
            </div>
          </section>

          {/* Demographics — hidden in individual mode (single client, no segmentation) */}
          {deploymentMode === "organization" && (
            <DemographicsSection
              demos={demos}
              updateDemo={updateDemo}
              removeDemoRow={removeDemoRow}
              addDemoRow={addDemoRow}
            />
          )}

          {/* Sprint 11 — Individual mode collects client name/email +
              required coach assignment in place of demographics. */}
          {deploymentMode === "individual" && (
            <section data-testid="individual-client-section">
              <h2 className="text-base mb-4" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", color: "#2563EB" }}>
                Client
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="label">Client name</label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Jane Citizen"
                    data-testid="client-name"
                    style={{
                      width: "100%", maxWidth: 420, padding: "10px 12px",
                      border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label className="label">Client email</label>
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="jane@example.com"
                    data-testid="client-email"
                    style={{
                      width: "100%", maxWidth: 420, padding: "10px 12px",
                      border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label className="label">Assigned coach</label>
                  <select
                    value={assignedCoachId}
                    onChange={(e) => setAssignedCoachId(e.target.value)}
                    data-testid="assigned-coach"
                    aria-label="Assigned coach"
                    style={{
                      maxWidth: 420, padding: "10px 12px",
                      border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                    }}
                  >
                    {coachOptions.length === 0 && <option value="">Loading…</option>}
                    {coachOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Only the assigned coach (and super-admins) will see this client&rsquo;s identified data.
                  </p>
                </div>
              </div>
            </section>
          )}

          {generatedToken && (
            <section
              className="panel"
              data-testid="generated-link-panel"
              style={{ borderColor: "#2563EB", background: "#FBFAF7" }}
            >
              <div className="eyebrow mb-2" style={{ color: "#2563EB" }}>
                Generate client link
              </div>
              <p className="text-sm mb-3">
                Survey link for <strong>{clientName}</strong>. Copy and share with your client.
              </p>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <input
                  readOnly
                  value={`${window.location.origin}/survey/${generatedToken}`}
                  data-testid="generated-link-input"
                  style={{
                    flex: "1 1 320px", padding: "10px 12px",
                    border: "1px solid #DDDDDD", background: "white", fontSize: "13px",
                    fontFamily: "monospace",
                  }}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/survey/${generatedToken}`)}
                  data-testid="copy-generated-link"
                  className="btn-quiet"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  data-testid="generated-link-done"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: "11px", color: "#FBFAF7", background: "#1A1A1A",
                    border: 0, padding: "10px 16px", cursor: "pointer",
                  }}
                >
                  Done
                </button>
              </div>
              <SendLinkButton
                deploymentId={createdDeploymentId}
                clientName={clientName}
                variant="primary"
              />
            </section>
          )}

          <div className="pt-6 border-t border-gray-100 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting || !!generatedToken}
              data-testid="create-submit"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7", background: "#AD393E",
                border: 0, padding: "12px 22px", cursor: submitting ? "wait" : "pointer",
              }}
            >
              {submitting
                ? "Creating…"
                : deploymentMode === "individual"
                ? "Generate client link"
                : "Create deployment"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="btn-quiet"
              data-testid="create-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
